import type { FormEvent } from "react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FaEdit, FaHome, FaSave, FaTimes, FaUserFriends } from "react-icons/fa";
import { AuthContext } from "../../../AuthProvider";
import PageTemplate from "../../../components/PageTemplate";
import SlidePageTransition from "../../../components/transitions/SlidePageTransition";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import ErrorPage from "../../../components/ErrorPage";
import FamilyForm from "../components/FamilyForm";
import { fetchFamily, fetchParentOptions, updateFamily } from "../api";
import type { AppError, Family, ParentOption } from "../types";
import { useFamilyForm } from "../hooks/useFamilyForm";
import { hasFamilyFormErrors, mapFamilyToFormValues, mapFormToFamilyPayload, validateFamilyForm } from "../utils/familyFormMapping";
import { familyDetailsPath, navigateFamilyFlow } from "../navigation";
import { familySituationLabel, hasSeparateHouseholds } from "../../../utils/familySituation";
import { mapHttpErrorToMessage } from "../../../utils/mapHttpErrorToMessage";
import {
  getCachedFamily,
  setCachedFamilyDetails,
  upsertCachedFamily,
} from "../../familyOverviewCache";

import BackButton, { useLeaveForm } from "../../../components/navigation/BackButton";
import { useFormDirtyState, useUnsavedChangesGuard } from "../../../components/navigation/UnsavedChangesGuard";
interface FamilyEditModal {
  show: boolean;
  type: string;
  title: string;
  message: string;
  navigateOnClose?: string;
}

const initialsFor = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

const FamilyEdit = () => {
  const { id = "" } = useParams();
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const cachedFamily = id ? getCachedFamily(id) : null;
  const { values, setValues, errors, setErrors, updateField, updateParentSearch, selectParent, swapParents } = useFamilyForm(mapFamilyToFormValues(cachedFamily));
  const [family, setFamily] = useState<Family | null>(() => cachedFamily);
  const [parents, setParents] = useState<ParentOption[]>([]);
  const [loading, setLoading] = useState(() => !cachedFamily);
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<FamilyEditModal>({ show: false, type: "", title: "", message: "" });
  const formDirtyRef = useRef(false);
  const { isDirty, markClean } = useFormDirtyState(mapFormToFamilyPayload(values));
  const { leaveWithoutWarning } = useUnsavedChangesGuard(isDirty);
  const initialCachedFamilyRef = useRef(Boolean(cachedFamily));
  const formId = "family-edit-form";

  useEffect(() => {
    if (!keycloak?.token) {
        setError({ message: t("common.auth_required", { defaultValue: "Authentication required." }), status: 401 });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        if (!initialCachedFamilyRef.current) setLoading(true);
        const [familyData, parentData] = await Promise.all([fetchFamily(id), fetchParentOptions()]);
        if (!cancelled) {
          setFamily(familyData);
          setParents(parentData);
          upsertCachedFamily(familyData);
          setCachedFamilyDetails(id, {
            family: familyData,
            currentYearFee: null,
          });
          if (!formDirtyRef.current) {
            const loadedValues = mapFamilyToFormValues(familyData, parentData);
            setValues(loadedValues);
            markClean(mapFormToFamilyPayload(loadedValues));
          }
        }
      } catch (loadError) {
        const mapped = mapHttpErrorToMessage(loadError, t) as AppError;
        if (!cancelled) setError({ message: mapped.message || t("family.edit.load_error", { defaultValue: "Could not load family." }), status: mapped.status });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, keycloak?.token, markClean, setValues, t]);

  useEffect(() => {
    if (!loading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading]);

  const setFamilyName = (value: string) => {
    formDirtyRef.current = true;
    updateField("familyName", value);
  };

  const setFamilySituation = (value: string) => {
    formDirtyRef.current = true;
    updateField("familySituation", value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateFamilyForm(values);
    setErrors(nextErrors);
    if (hasFamilyFormErrors(nextErrors)) {
      setModal({
        show: true,
        type: "error",
        title: t("common.validation_error", { defaultValue: "Validation Error" }),
        message: t("family.new.validation_error", { defaultValue: "Fill in all required fields." }),
      });
      return;
    }

    try {
      setSaving(true);
      const updated = await updateFamily(id, mapFormToFamilyPayload(values));
      setFamily((current) => ({ ...(current ?? updated), ...updated }));
      upsertCachedFamily(updated);
      setCachedFamilyDetails(id, {
        family: updated,
        currentYearFee: null,
      });
      const savedValues = mapFamilyToFormValues(updated, parents);
      setValues(savedValues);
      markClean(mapFormToFamilyPayload(savedValues));
      formDirtyRef.current = false;
      setModal({
        show: true,
        type: "success",
        title: t("common.modal.confirm", { defaultValue: "Confirm" }),
        message: t("family.edit.success", { defaultValue: "Family updated successfully." }),
        navigateOnClose: familyDetailsPath(id),
      });
    } catch (submitError) {
      const mapped = mapHttpErrorToMessage(submitError, t) as AppError;
      setModal({
        show: true,
        type: "error",
        title: t("common.modal.confirm", { defaultValue: "Confirm" }),
        message: mapped.message || t("family.edit.error", { defaultValue: "Could not update the family." }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  if (!family) return <ErrorPage message={t("family.details.not_found", { defaultValue: "Family not found." })} status={undefined} onRetry={() => window.location.reload()} />;

  const familyName = values.familyName.trim();
  const connectedParents = [values.selectedParents.parent1, values.selectedParents.parent2].filter(Boolean).length;

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-white sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={familyDetailsPath(String(id))} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => leaveWithoutWarning(() => navigateFamilyFlow(navigate, familyDetailsPath(id), "back"))}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
            >
              <FaTimes aria-hidden="true" />
              {t("common.actions.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              form={formId}
              className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:text-white"
              disabled={saving}
            >
              <FaSave aria-hidden="true" />
              {saving ? t("common.loading", { defaultValue: "Loading..." }) : t("common.actions.save_changes", { defaultValue: "Save changes" })}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <header className="relative overflow-hidden rounded-[24px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/70 p-5 shadow-[0_18px_45px_rgba(67,56,202,0.12)] dark:border-indigo-400/25 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-6">
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] opacity-75 lg:block">
              <div className="absolute bottom-4 right-7 h-20 w-48 rounded-t-full bg-sky-100/75 dark:bg-sky-300/10" />
              <div className="absolute bottom-4 right-24 h-28 w-40 rounded-t-full bg-indigo-100/70 dark:bg-indigo-300/10" />
              <div className="absolute bottom-8 right-44 grid grid-cols-5 gap-2 opacity-55">
                {Array.from({ length: 25 }).map((_, index) => (
                  <span key={index} className="h-1.5 w-1.5 rounded-full bg-indigo-300/70 dark:bg-indigo-200/30" />
                ))}
              </div>
            </div>
            <div className="relative flex flex-col gap-5 md:flex-row md:items-center">
              <div className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-50 text-3xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100 sm:h-32 sm:w-32">
                {familyName ? initialsFor(familyName) : <FaHome aria-hidden="true" />}
              </div>
              <div className="min-w-0">
                <h1 className="break-words text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
                  {familyName || t("family.edit.title", { defaultValue: "Edit family" })}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${
                      hasSeparateHouseholds(values.familySituation)
                        ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100"
                        : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-100"
                    }`}
                  >
                    {familySituationLabel(t, values.familySituation)}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <FaUserFriends aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {t("family.details.parents", { defaultValue: "Parents" })}: {connectedParents}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <FaEdit aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
                    {t("family.edit.title", { defaultValue: "Edit family" })}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <FamilyForm
            formId={formId}
            hideActions
            mode="edit"
            values={values}
            errors={errors}
            saving={saving}
            parents={parents}
            onFamilyNameChange={setFamilyName}
            onFamilySituationChange={setFamilySituation}
            onParentSearchChange={(slot, value) => {
              formDirtyRef.current = true;
              updateParentSearch(slot, value);
            }}
            onSelectParent={(slot, parent) => {
              formDirtyRef.current = true;
              selectParent(slot, parent);
            }}
            onSwapParents={() => {
              formDirtyRef.current = true;
              swapParents();
            }}
            onSubmit={handleSubmit}
            onCancel={() => leaveWithoutWarning(() => navigateFamilyFlow(navigate, familyDetailsPath(id), "back"))}
            t={t}
          />
        </div>
      </SlidePageTransition>

      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const target = modal.navigateOnClose;
          setModal({ show: false, type: "", title: "", message: "" });
          if (target) leaveForm(target, { state: { direction: "back" } });
        }}
      />
    </PageTemplate>
  );
};

export default FamilyEdit;
